import { Logger } from '@/model/LoggingModel';
import { DigiTracPermission, DigiTracPermissionItem } from '@/model/RolesModel';
import { UndoOutlined } from '@ant-design/icons';
import { Button } from 'antd';
import React, { useContext, useRef, useState } from 'react';
import { batch } from 'react-redux';
import { deviceAdminApi } from '../../../../../api';
import { handleResponse } from '../../../../../Helper';
import { SelectOptions } from '../../../../../model/CommonModel';
import { ApplicationPermission, ApplicationSubPermission, RoleTab, UserRole } from '../../../../../model/DeviceAdminModel';
import { ModalConfirmCustomFooter, Select, SelectOption } from '../../../../common';
import { getDefaultDigiTracPermission } from '../helper';
import { RoleStore } from '../RoleContext/context';
import styles from './cloneFromRole.module.scss';

type Props<T, K> = {
	tab: RoleTab;
	setLoading: (isLoading: boolean) => void;
	getData?: () => K;
	setData: (isDirty: boolean, data: T) => void;
};

type ActionsByTab = {
	[Role in RoleTab]: {
		setDataFromRole: (isDirty: boolean, roleId: number) => void;
		emptyPermissions: () => void;
	};
};

const CloneFromRole = <
	T extends object | object[] | string | string[] | number | number[] | Record<number, number[]>,
	K extends object | object[] | string | string[] | number | number[] | Record<number, number[]>
>({
	tab,
	setLoading,
	getData,
	setData,
}: Props<T, K>) => {
	//#region state management variables
	const {
		roleContextInitialState: {
			roles,
			props: { roleId },
		},
	} = useContext(RoleStore);

	const [selectedRoleId, setSelectedRoleId] = useState<number>(undefined);
	const [isEnabledBackToPreviousState, setIsEnabledBackToPreviousState] = useState<boolean>(true);
	const [showModalApplyFrom, setShowModalApplyFrom] = useState<boolean>(false);
	const [showModalBackToPreviousState, setShowModalBackToPreviousState] = useState<boolean>(false);
	const [showModalBackToDefault, setShowModalBackToDefault] = useState<boolean>(false);
	const selectedRoleNameRef = useRef<string>('');
	//#endregion

	const copyActionsByTab: ActionsByTab = {
		[RoleTab.MembersPermission]: {
			setDataFromRole: (isDirty, roleId) => {
				deviceAdminApi
					.getMembersIdsInRole(roleId)
					.then(res => {
						if (!handleResponse(res)) {
							const {
								Entity: { SubscribedOperatorIds },
							} = res;
							setData(isDirty, SubscribedOperatorIds as T);
						}
					})
					.catch(err => {
						Logger.writeErrorLog(`${err.name}: ${err.message}`);
					})
					.finally(() => {
						setLoading(false);
					});
			},
			emptyPermissions: () => {
				batch(() => {
					setData(true, [] as T);
					setLoading(false);
				});
			},
		},
		[RoleTab.ApplicationPermission]: {
			setDataFromRole: (isDirty, roleId) => {
				deviceAdminApi
					.getApplicationPermissions(roleId)
					.then(res => {
						if (!handleResponse(res)) {
							const { Entity } = res;
							Entity.forEach((x: ApplicationPermission) =>
								x.ApplicationSubPermissions.forEach((y: ApplicationSubPermission) => {
									y.key = y.SubComponentId;
								})
							);
							setData(isDirty, Entity as T);
						}
					})
					.catch(err => {
						Logger.writeErrorLog(`${err.name}: ${err.message}`);
					})
					.finally(() => {
						setLoading(false);
					});
			},
			emptyPermissions: () => {
				batch(() => {
					const mappedApplicationPermissions: ApplicationPermission[] = getData() as ApplicationPermission[];
					setData(
						true,
						mappedApplicationPermissions.map((perm: ApplicationPermission) => ({
							...perm,
							ApplicationSubPermissions: perm.ApplicationSubPermissions.map((subPerm: ApplicationSubPermission) => ({
								...subPerm,
								IsChecked: false,
							})),
						})) as T
					);
					setLoading(false);
				});
			},
		},
		[RoleTab.GeneralPermission]: {
			setDataFromRole: (isDirty, roleId) => {
				deviceAdminApi
					.getGeneralPermissions(roleId)
					.then(res => {
						if (!handleResponse(res)) {
							const {
								Entity: { GrantedSubPermissions },
							} = res;
							setData(isDirty, GrantedSubPermissions as T);
						}
					})
					.catch(err => {
						Logger.writeErrorLog(`${err.name}: ${err.message}`);
					})
					.finally(() => {
						setLoading(false);
					});
			},
			emptyPermissions: () => {
				const mappedGeneralPermissions: Record<number, number[]> = { ...(getData() as Record<number, number[]>) };
				Object.keys(mappedGeneralPermissions).forEach((key: string) => (mappedGeneralPermissions[Number(key)] = []));
				batch(() => {
					setData(true, mappedGeneralPermissions as T);
					setLoading(false);
				});
			},
		},
		[RoleTab.HardwarePermission]: {
			setDataFromRole: (isDirty, targetRoleId) => {
				deviceAdminApi
					.getHardwarePermission(targetRoleId)
					.then(async res => {
						if (!handleResponse(res)) {
							const { Entity } = res;
							let modifiedHardwarePermissions: Record<number, DigiTracPermissionItem> = [];
							if (isDirty) {
								const permissionDifferenceResponse = await deviceAdminApi.getHardwarePermissionDifference(roleId, targetRoleId);
								if (!handleResponse(permissionDifferenceResponse)) {
									modifiedHardwarePermissions = permissionDifferenceResponse.Entity;
								}
							}
							setData(isDirty, {
								roleId: targetRoleId,
								modifiedHardwarePermissions: modifiedHardwarePermissions,
								availableHardwarePermissions: Entity,
							} as T);
						}
					})
					.catch(err => {
						Logger.writeErrorLog(`${err.name}: ${err.message}`);
					})
					.finally(() => {
						setLoading(false);
					});
			},
			emptyPermissions: () => {
				deviceAdminApi
					.getHardwarePermissionDifference(roleId, 0)
					.then(response => {
						if (!handleResponse(response)) {
							const { Entity } = response;

							const originalDigiTracPermission = getData() as DigiTracPermission;
							setData(true, {
								roleId: roleId,
								modifiedHardwarePermissions: Entity,
								availableHardwarePermissions: {
									...getDefaultDigiTracPermission(),
									ControllerDefinitions: originalDigiTracPermission.ControllerDefinitions,
								},
							} as T);
						}
					})
					.catch(err => {
						Logger.writeErrorLog(`${err.name}: ${err.message}`);
					})
					.finally(() => {
						setLoading(false);
					});
			},
		},
	};

	/**
	 * Set the data from the selected roleId
	 * @param value stands up for role id
	 */
	const onCopyFromSelectionChanged = (value: number, isResettingLastState?: boolean) => {
		setLoading(true);
		batch(() => {
			if (isResettingLastState) {
				setShowModalBackToPreviousState(true);
			} else {
				if (value) {
					setSelectedRoleId(value);
					setShowModalApplyFrom(true);
					selectedRoleNameRef.current = roles.find(role => role.Id === Number(value))?.Name;
				}
			}
		});
	};

	const applyCopyFrom = () => {
		return (
			<ModalConfirmCustomFooter
				title={`Copy from ${selectedRoleNameRef.current}`}
				width='600px'
				visible={showModalApplyFrom}
				okText='Yes, continue'
				cancelText='No, cancel'
				onOk={() => {
					batch(() => {
						copyActionsByTab[tab].setDataFromRole(true, selectedRoleId);
						setIsEnabledBackToPreviousState(false);
						setShowModalApplyFrom(false);
						selectedRoleNameRef.current = '';
					});
				}}
				onCancel={() => {
					batch(() => {
						setSelectedRoleId(undefined);
						setShowModalApplyFrom(false);
						setLoading(false);
						selectedRoleNameRef.current = '';
					});
				}}
				isNoButtonHide={true}
				children={
					<>
						<span>
							<p className={styles.modalText}>{_('AreYouSureYouWantToApplyThePermissions')}</p>
							<p className={styles.modalText}>{_('AreYouSureYouWantToContinue')}</p>
						</span>
					</>
				}
			/>
		);
	};

	const backToOriginal = () => {
		return (
			<ModalConfirmCustomFooter
				title={_('RevertToOriginalConfirmation')}
				width='600px'
				visible={showModalBackToPreviousState}
				okText='Yes, continue'
				cancelText='No, cancel'
				onOk={() => {
					batch(() => {
						copyActionsByTab[tab].setDataFromRole(false, roleId);
						setIsEnabledBackToPreviousState(true);
						setShowModalBackToPreviousState(false);
						setSelectedRoleId(undefined);
					});
				}}
				onCancel={() => {
					batch(() => {
						setSelectedRoleId(undefined);
						setShowModalBackToPreviousState(false);
						setLoading(false);
					});
				}}
				isNoButtonHide={true}
				children={
					<>
						<span>
							<p className={styles.modalText}>{_('IfYouDoAllCurrentSelectionsWillBeDiscarded')}</p>
							<p className={styles.modalText}>{_('AreYouSureYouWantToContinue')}</p>
						</span>
					</>
				}
			/>
		);
	};

	const onEmptyPermissions = () => {
		return (
			<ModalConfirmCustomFooter
				title={_('ClearAll')}
				width='600px'
				visible={showModalBackToDefault}
				okText='Yes, continue'
				cancelText='No, cancel'
				onOk={() => {
					batch(() => {
						copyActionsByTab[tab].emptyPermissions();
						setIsEnabledBackToPreviousState(false);
						setShowModalBackToDefault(false);
					});
				}}
				onCancel={() => {
					batch(() => {
						setSelectedRoleId(undefined);
						setShowModalBackToDefault(false);
						setLoading(false);
					});
				}}
				isNoButtonHide={true}
				children={
					<>
						<span>
							<p className={styles.modalText}>{_('ByTakingThisActionAllPermissions')}</p>
							<p className={styles.modalText}>{_('AreYouSureYouWantToContinue')}</p>
						</span>
					</>
				}
			/>
		);
	};

	const options: SelectOption = [
		...roles
			.filter(d => d.Id !== roleId)
			.map<SelectOptions<number>>((role: UserRole) => ({
				id: `roleOption-${role.Id}`,
				label: role.Name,
				value: role.Id,
			})),
	];

	return roleId === 1 ? (
		<></>
	) : (
		<div className={styles.copyFromContainer}>
			<label id='cloneFromRolePermissionsLabel' htmlFor='cloneFromRolePermissionsSelect'>
				{_('CopyFrom')}
			</label>
			<Select
				id='cloneFromRolePermissionsSelect'
				className={styles.copyFromSelector}
				value={selectedRoleId}
				options={options}
				onChange={(copyFromSelection: number) => onCopyFromSelectionChanged(copyFromSelection, false)}
			/>
			<Button
				id='cloneFromRolePermissionsRevertToOriginal'
				className={styles.backPreviousState}
				disabled={isEnabledBackToPreviousState}
				icon={<UndoOutlined />}
				onClick={() => onCopyFromSelectionChanged(selectedRoleId, true)}>
				{_('RevertToOriginal')}
			</Button>
			<Button id='cloneFromRolePermissionsClearAll' className={styles.backPreviousState} onClick={() => setShowModalBackToDefault(true)}>
				{_('ClearAll')}
			</Button>
			{applyCopyFrom()}
			{backToOriginal()}
			{onEmptyPermissions()}
		</div>
	);
};

export { CloneFromRole };
